import React, { useState, useEffect, forwardRef } from 'react';

/**
 * A highly customizable counter component
 *
 * @param {Object} props - Component props
 * @param {number} [props.initialValue=0] - Initial count value
 * @param {number} [props.minCount=0] - Minimum allowed count
 * @param {number} [props.maxCount=Infinity] - Maximum allowed count
 * @param {number} [props.step=1] - Step value for increment/decrement
 * @param {Function} [props.onChange] - Callback when count changes
 * @param {string} [props.variant="default"] - Visual variant (default, green, minimal, outlined, custom)
 * @param {string} [props.className=""] - Additional CSS classes
 * @param {boolean} [props.disabled=false] - Whether the counter is disabled
 * @param {boolean} [props.controlled=true] - Whether to use controlled component pattern
 * @param {number} [props.value] - External value (when controlled)
 * @param {number} [props.size="md"] - Size variant (sm, md, lg)
 * @param {string} [props.incrementLabel="+"] - Label for increment button
 * @param {string} [props.decrementLabel="-"] - Label for decrement button
 * @param {Object} [props.customStyles] - Custom style overrides
 * @param {string} [props.testId] - Test ID for testing
 * @param {React.Ref} ref - Forwarded ref
 */
export const Counter = forwardRef(
  (
    {
      initialValue = 0,
      minCount = 0,
      maxCount = Infinity,
      step = 1,
      onChange,
      variant = 'default',
      className = '',
      disabled = false,
      controlled = true,
      value,
      size = 'md',
      incrementLabel = '+',
      decrementLabel = '-',
      customStyles = {},
      testId = 'counter',
    },
    ref,
  ) => {
    // State management based on controlled vs uncontrolled
    const [internalCount, setInternalCount] = useState(initialValue);
    const count = controlled && typeof value !== 'undefined' ? value : internalCount;

    // Update internal state if controlled value changes
    useEffect(() => {
      if (controlled && typeof value !== 'undefined') {
        setInternalCount(value);
      }
    }, [controlled, value]);

    // Handle increment with step
    const increment = () => {
      if (count + step <= maxCount && !disabled) {
        const newCount = count + step;
        if (!controlled) setInternalCount(newCount);
        if (onChange) onChange(newCount);
      }
    };

    // Handle decrement with step
    const decrement = () => {
      if (count - step >= minCount && !disabled) {
        const newCount = count - step;
        if (!controlled) setInternalCount(newCount);
        if (onChange) onChange(newCount);
      }
    };

    // Size configurations
    const sizeConfig =
      {
        sm: {
          buttonSize: 'w-5 h-5',
          fontSize: 'text-xs',
          counterWidth: 'w-8',
        },
        md: {
          buttonSize: 'w-6 h-6',
          fontSize: 'text-xs sm:text-sm',
          counterWidth: 'w-10 sm:w-12',
        },
        lg: {
          buttonSize: 'w-8 h-8',
          fontSize: 'text-sm sm:text-base',
          counterWidth: 'w-14 sm:w-16',
        },
      }[size] || sizeConfig.md;

    // Style configurations based on variant
    const variantStyles = {
      default: {
        container: 'bg-white',
        text: 'text-black',
        decrementBtn: `${
          count > minCount ? 'bg-primary hover:bg-secondary' : 'bg-gray-300'
        } text-white`,
        incrementBtn: `${
          count < maxCount ? 'bg-primary hover:bg-secondary' : 'bg-gray-300'
        } text-white`,
      },
      green: {
        container: 'bg-primary',
        text: 'text-white',
        decrementBtn: 'bg-white text-green-500 hover:bg-gray-100',
        incrementBtn: 'bg-white text-green-500 hover:bg-gray-100',
      },
      minimal: {
        container: 'bg-transparent',
        text: 'text-black',
        decrementBtn: 'bg-gray-100 hover:bg-gray-200 text-black',
        incrementBtn: 'bg-gray-100 hover:bg-gray-200 text-black',
      },
      outlined: {
        container: 'border border-gray-300 rounded-md px-2',
        text: 'text-black',
        decrementBtn: 'border border-gray-300 bg-white hover:bg-gray-100 text-black',
        incrementBtn: 'border border-gray-300 bg-white hover:bg-gray-100 text-black',
      },
      custom: customStyles,
    };

    const styles = variantStyles[variant] || variantStyles.default;

    // Handle disabled state
    const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
    const decrementDisabled = count <= minCount || disabled;
    const incrementDisabled = count >= maxCount || disabled;

    return (
      <div
        className={`inline-flex items-center ${styles.container} ${className} ${disabledClasses}`}
        data-testid={testId}
        ref={ref}
      >
        <button
          onClick={decrement}
          className={`${sizeConfig.buttonSize} flex items-center justify-center rounded-full ${
            styles.decrementBtn
          } transition font-semibold ${decrementDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={decrementDisabled}
          aria-label="Decrease quantity"
          data-testid={`${testId}-decrement`}
        >
          {decrementLabel}
        </button>

        <span
          className={`${sizeConfig.counterWidth} text-center ${sizeConfig.fontSize} font-semibold ${styles.text}`}
          data-testid={`${testId}-value`}
        >
          {count}
        </span>

        <button
          onClick={increment}
          className={`${sizeConfig.buttonSize} flex items-center justify-center rounded-full ${
            styles.incrementBtn
          } transition font-semibold ${incrementDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          disabled={incrementDisabled}
          aria-label="Increase quantity"
          data-testid={`${testId}-increment`}
        >
          {incrementLabel}
        </button>
      </div>
    );
  },
);

// Display name for debugging
Counter.displayName = 'Counter';

export default Counter;
